// =============================================================================
// BankDepositsPage — combined list + create
// =============================================================================
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiGet, apiPost, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { IconButton } from '../../../components/IconButton';
import { Input } from '../../../components/Input';
import { Money } from '../../../components/Money';
import { EmailDocButton } from '../../../components/EmailDocButton';
import { PageScaffold } from '../../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../../components/DataTable';

type Kind = 'bank' | 'cash_drawer' | 'petty_cash' | 'mobile' | 'card' | 'cheque' | 'in_transit';

interface BankAccountOption {
  id: string;
  name: string;
  kind: Kind;
  reference: string | null;
  account: { code: string; name: string };
}
interface DepositRow {
  id: string;
  number: string;
  status: 'DRAFT' | 'POSTED';
  totalAmount: string;
  reference: string | null;
  depositedAt: string;
  targetAccount: { id: string; name: string };
  createdBy: { id: string; name: string };
  lines: Array<{ id: string; amount: string; sourceAccount: { id: string; name: string } }>;
}

interface DepositLine {
  sourceBankAccountId: string;
  amount: string;
  notes: string;
}

export function BankDepositsPage(): JSX.Element {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const list = useQuery({
    queryKey: ['bank-deposits'],
    queryFn: () => apiGet<DepositRow[]>('/bank-deposits'),
    refetchInterval: 60_000,
  });

  const HEADER_ACTIONS = (
    <Button onClick={() => setShowForm((v) => !v)}>
      {showForm ? 'Cancel' : '+ New deposit slip'}
    </Button>
  );

  return (
    <PageScaffold
      title="Bank Deposits"
      subtitle="HQ cash + cheques → bank. Posts Dr Bank, Cr each source."
      actions={HEADER_ACTIONS}
      scroll={false}
    >
      {showForm && (
        <div className="shrink-0">
          <NewDepositForm
            onDone={() => {
              setShowForm(false);
              qc.invalidateQueries({ queryKey: ['bank-deposits'] });
            }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      <DataTableCard>
        {list.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : (list.data ?? []).length === 0 ? (
          <div className="text-center text-ink-3 px-6 py-12 text-sm italic">
            No deposits recorded yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">Slip</th>
                <th className="px-6 py-2 font-medium">Date</th>
                <th className="px-6 py-2 font-medium">To bank</th>
                <th className="px-6 py-2 font-medium">Sources</th>
                <th className="px-6 py-2 font-medium">Bank ref</th>
                <th className="px-6 py-2 font-medium text-right">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((d) => (
                <tr key={d.id} className="border-b border-rule-2 hover:bg-paper-2">
                  <td className="px-6 py-3 kc-mono text-xs text-ink">{d.number}</td>
                  <td className="px-6 py-3 text-xs text-ink-3">
                    {new Date(d.depositedAt).toLocaleDateString('en-MU', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </td>
                  <td className="px-6 py-3 text-ink">{d.targetAccount.name}</td>
                  <td className="px-6 py-3 text-xs text-ink-2">
                    {d.lines.length} source{d.lines.length === 1 ? '' : 's'}:{' '}
                    {d.lines.map((l) => l.sourceAccount.name).join(', ')}
                  </td>
                  <td className="px-6 py-3 text-xs text-ink-3 kc-mono">{d.reference ?? '—'}</td>
                  <td className="px-6 py-3 text-right">
                    <Money value={d.totalAmount} />
                  </td>
                  <td className="px-6 py-3 text-right space-x-2">
                    <Button
                      variant="tertiary"
                      size="xs"
                      onClick={() => downloadSlip(d.id, d.number)}
                    >
                      PDF
                    </Button>
                    <EmailDocButton
                      endpoint={`/bank-deposits/${d.id}/email`}
                      selfDefault
                      title={`Email deposit slip ${d.number}`}
                      subtitle="Send the bank deposit slip PDF by email."
                      renderTrigger={(open) => (
                        <Button variant="tertiary" size="xs" onClick={open}>
                          Email
                        </Button>
                      )}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}

async function downloadSlip(id: string, number: string): Promise<void> {
  try {
    const res = await api.get(`/bank-deposits/${id}/pdf`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  } catch (e) {
    console.error(e);
  }
}

function NewDepositForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [targetBankAccountId, setTarget] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DepositLine[]>([
    { sourceBankAccountId: '', amount: '', notes: '' },
  ]);
  const [err, setErr] = useState<string | null>(null);

  // Fetch bank accounts to populate dropdowns. The /bank-deposits endpoint
  // doesn't expose this, but /accounting/* might. For simplicity, derive
  // candidates from the company's accounts via a dedicated bank-accounts
  // endpoint if available; falling back to a manual list isn't possible without
  // one — so let's add one inline using accounting endpoint:
  // We use /accounting/journal as a placeholder is silly. Use a dedicated
  // bank-accounts endpoint already exists indirectly via /company/commission-rules
  // (no). For dev, fetch via /accounting/trial-balance and read accounts;
  // for now just rely on a hardcoded shape from /bank-accounts not existing.
  // Easiest: add a quick GET /bank-accounts later. For now, query /accounting/trial-balance
  // and filter codes that look like bank/cash accounts.
  const banks = useQuery({
    queryKey: ['bank-accounts-list'],
    queryFn: () =>
      apiGet<BankAccountOption[]>('/bank-deposits/_bank-accounts').catch(
        () => [] as BankAccountOption[],
      ),
  });

  const targets = useMemo(() => (banks.data ?? []).filter((b) => b.kind === 'bank'), [banks.data]);
  const sources = useMemo(
    () =>
      (banks.data ?? []).filter((b) =>
        ['cash_drawer', 'petty_cash', 'cheque', 'in_transit'].includes(b.kind),
      ),
    [banks.data],
  );

  const total = useMemo(() => lines.reduce((s, l) => s + (Number(l.amount) || 0), 0), [lines]);

  const post = useMutation({
    mutationFn: () =>
      apiPost('/bank-deposits', {
        targetBankAccountId,
        reference: reference || undefined,
        notes: notes || undefined,
        lines: lines
          .filter((l) => l.sourceBankAccountId && Number(l.amount) > 0)
          .map((l) => ({
            sourceBankAccountId: l.sourceBankAccountId,
            amount: Number(l.amount),
            notes: l.notes || undefined,
          })),
      }),
    onSuccess: onDone,
    onError: (e) => setErr(getApiError(e).message),
  });

  const canPost =
    !!targetBankAccountId &&
    lines.some((l) => l.sourceBankAccountId && Number(l.amount) > 0) &&
    total > 0;

  return (
    <Card
      title="New deposit slip"
      subtitle="Fill target bank, source(s) + amount(s). Slip posts immediately on Save."
    >
      {banks.isLoading ? (
        <div className="text-xs text-ink-3">Loading bank accounts…</div>
      ) : (banks.data ?? []).length === 0 ? (
        <div className="text-xs text-warning">
          No bank accounts endpoint exposed. Backend tip: add a thin{' '}
          <code>GET /bank-deposits/_bank-accounts</code> handler returning BankAccount[] with
          .account.code/.name. The form is wired but needs that endpoint to populate dropdowns.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Deposit to
              </span>
              <select
                value={targetBankAccountId}
                onChange={(e) => setTarget(e.target.value)}
                className="kc-input"
              >
                <option value="">— pick bank account —</option>
                {targets.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.account.code}){b.reference ? ` · ${b.reference}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <Input
              label="Bank reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Slip number from bank counter"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Sources
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setLines((cur) => [...cur, { sourceBankAccountId: '', amount: '', notes: '' }])
                }
              >
                + Add source
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                  <select
                    value={l.sourceBankAccountId}
                    onChange={(e) =>
                      setLines((cur) =>
                        cur.map((x, i) =>
                          i === idx ? { ...x, sourceBankAccountId: e.target.value } : x,
                        ),
                      )
                    }
                    className="kc-input col-span-5"
                  >
                    <option value="">— pick source —</option>
                    {sources.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.account.code})
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Amount"
                    value={l.amount}
                    onChange={(e) =>
                      setLines((cur) =>
                        cur.map((x, i) => (i === idx ? { ...x, amount: e.target.value } : x)),
                      )
                    }
                    className="kc-input col-span-3 text-right"
                  />
                  <input
                    type="text"
                    placeholder="Note (e.g. 3 cheques)"
                    value={l.notes}
                    onChange={(e) =>
                      setLines((cur) =>
                        cur.map((x, i) => (i === idx ? { ...x, notes: e.target.value } : x)),
                      )
                    }
                    className="kc-input col-span-3"
                  />
                  <IconButton
                    aria-label="Remove line"
                    variant="danger"
                    size="sm"
                    onClick={() =>
                      setLines((cur) => (cur.length > 1 ? cur.filter((_, i) => i !== idx) : cur))
                    }
                    className="col-span-1"
                  >
                    ×
                  </IconButton>
                </div>
              ))}
            </div>
          </div>

          <Input
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <div className="flex items-center justify-between pt-2 border-t border-rule">
            <div className="text-sm">
              Total:{' '}
              <span className="text-xl kc-serif text-accent">
                <Money value={total} />
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setErr(null);
                  post.mutate();
                }}
                disabled={!canPost || post.isPending}
              >
                {post.isPending ? 'Posting…' : 'Post deposit slip'}
              </Button>
            </div>
          </div>
          {err && <div className="text-xs text-negative">{err}</div>}
        </div>
      )}
    </Card>
  );
}
